/**
 * Аватар участника — одним компонентом на всё приложение.
 *
 * Раньше аватар собирался на месте из Avatar и AvatarFallback в двух десятках
 * файлов, и загруженную картинку пришлось бы добавлять в каждый. Один
 * компонент означает, что она появляется везде сразу и везде одинаково: в
 * списке участников, на карточке задачи, в календаре, в обсуждении, в журнале
 * и в шапке.
 *
 * Пропорции держит сам компонент. Аватар всегда круглый и всегда квадратный
 * по исходнику: `aspect-square` плюс `object-cover` — картинка кадрируется по
 * центру, а не сплющивается. Это особенно важно в календаре, где размер
 * шесть пикселей и любое искажение бросается в глаза.
 *
 * Нет картинки — остаётся прежний круг с инициалами, окрашенный по человеку.
 */

import type { UserDto } from '@project-os/contracts';
import { mediaUrl } from '@/api/client';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { initials } from '@/lib/format';
import { memberColorStyle } from '@/lib/member-color';
import { cn } from '@/lib/utils';

/**
 * Кого рисуем.
 *
 * Обязательно только имя: часть выборок отдаёт участника одним полем — этого
 * хватает на инициалы. Без идентификатора не будет цвета участника, без
 * avatarUrl — картинки; и то и другое честнее, чем требовать поля, которых у
 * вызывающей стороны нет.
 */
export interface AvatarPerson {
  id?: string;
  name: string;
  avatarUrl?: string | null;
}

const SIZES = {
  // Для календарной клетки: строка задачи там высотой в полтора десятка
  // пикселей, и всё, что крупнее, ломает вёрстку дня. Инициалы на таком
  // размере не читаются — остаётся цветной круг, и это правильно: он
  // различает людей, а имя рядом их называет.
  '2xs': 'size-3 text-[0px]',
  xs: 'size-4 text-[7px]',
  sm: 'size-5 text-[8px]',
  md: 'size-6 text-2xs',
  lg: 'size-8 text-xs',
  xl: 'size-16 text-lg',
} as const;

export function UserAvatar({
  user,
  size = 'md',
  className,
  title,
}: {
  user: AvatarPerson | UserDto | null | undefined;
  size?: keyof typeof SIZES;
  className?: string;
  /** Подпись при наведении. По умолчанию — имя. */
  title?: string;
}) {
  if (!user) {
    return (
      <span
        aria-hidden
        className={cn(
          'inline-block shrink-0 rounded-full bg-muted-foreground/25',
          SIZES[size],
          className,
        )}
      />
    );
  }

  return (
    <Avatar
      className={cn('shrink-0', SIZES[size], className)}
      title={title ?? user.name}
      style={user.id ? memberColorStyle(user.id) : undefined}
    >
      {user.avatarUrl ? (
        // object-cover, а не растяжение: снимок с телефона прямоугольный, и
        // вписывать его в круг искажением нельзя.
        <AvatarImage
          src={mediaUrl(user.avatarUrl)}
          alt=""
          className="aspect-square size-full object-cover"
        />
      ) : null}
      <AvatarFallback
        // Цвет задаётся строчным стилем, а не классом.
        //
        // .member-color лежит в слое components, а bg-muted из базового
        // AvatarFallback — в слое utilities, и слой решает раньше
        // избирательности: класс «есть», но круг оставался серым, а поверх
        // него текст цвета фона. Контраст выходил 1.1 — инициалы не читались
        // вовсе. Строчный стиль побеждает любой слой.
        style={
          user.id
            ? {
                backgroundColor: 'oklch(var(--member-l-solid) var(--member-c) var(--member-h))',
                color: 'var(--background)',
              }
            : undefined
        }
        className={cn(
          !user.id && 'bg-muted text-muted-foreground',
          'font-medium',
          // Кегль наследуется от размера круга: инициалы не должны вылезать.
          'text-[length:inherit]',
        )}
      >
        {initials(user.name)}
      </AvatarFallback>
    </Avatar>
  );
}
